import './OffsetPaginator.scss';
import { DataSelector } from '@lib/selectors';
import { LoadingSpinner } from './LoadingSpinner';
import { When } from 'react-if';
import { ActionFactory, useOnMount, useSonarShallowSelector } from '@hooks';
import { OffsetPaginatedResource, OffsetPaginationParams } from '@sonar-software/sonar-ts-types';
import { Pagination, PaginationProps } from 'antd';
import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react';

export interface OffsetPaginatorProps<
	TRequest extends OffsetPaginationParams,
	TData extends OffsetPaginatedResource<unknown>
> extends PaginationProps {
	count?: number;
	loading?: boolean;
	offset?: number;
	render?: (data: TData | null | undefined) => React.ReactNode;
	request: ActionFactory<TRequest>;
	selector: DataSelector<TData | null | undefined>;
}

export const OffsetPaginator: React.FC<PropsWithChildren<
	OffsetPaginatorProps<OffsetPaginationParams, OffsetPaginatedResource<unknown>>>
> = ({ children, loading, render, request, selector, offset = 0, count = 10, ...rest }) => {
	const [ current, setCurrent ] = useState(1);
	const data = useSonarShallowSelector(selector);
	const showPagination = (data?.totalCount ?? 0) > (data?.results.length ?? 0);

	useOnMount(() => {
		request({
			count,
			offset
		});
	});

	useEffect(() => {
		setCurrent(offset && count ? Math.floor(offset / count) + 1 : 1);
	}, [ offset, count ]);

	const handleOnChange = useCallback(
		(page: number, pageSize: number) => {
			setCurrent(page);
			request({
				count: pageSize,
				offset: (page - 1) * pageSize
			});
		},
		[ request ]
	);

	return (
		<>
			<LoadingSpinner spinning={ loading || !data }>{ render ? render(data) : children }</LoadingSpinner>
			<When condition={ showPagination }>
				<Pagination
					current={ current }
					onChange={ handleOnChange }
					pageSize={ count }
					showSizeChanger={ false }
					total={ data?.totalCount }
					{ ...rest } />
			</When>
		</>
	);
};
